import type { ComponentProps } from "react";

import { Checkbox, Flex, Input, Text } from "@theorgchartdev/storybook";
import { GetFieldDefFromName } from "@/Utils/DataLib";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";

type CheckboxProps = ComponentProps<typeof Checkbox>;
type InputProps = ComponentProps<typeof Input>;

type FieldFormatPartial = {
  listOptions?: {
    delimiter: string;
    enabled: boolean;
  };
};

type Props = {
  fieldName?: string;
  fieldFormat: Nullish<FieldFormatPartial>;
  onEnable: () => void;
  onChange: <K extends keyof FieldFormatPartial>(key: K, value: FieldFormatPartial[K]) => void;
  dataTestIdPrefix?: string;
};

const defaultListOptions = {
  enabled: false,
  delimiter: ","
};

export const FieldListConfiguration = (props: Props) => {
  const listOptions = {
    ...defaultListOptions,
    ...props.fieldFormat?.listOptions
  };

  const handleToggleList: CheckboxProps["onToggle"] = event => {
    if (event.target.checked) {
      props.onEnable();
    }

    props.onChange("listOptions", {
      ...listOptions,
      enabled: event.target.checked
    });
  };

  const handleDelimiterChange: InputProps["onChange"] = (_, nextValue) => {
    if (nextValue.length > 1) {
      nextValue = nextValue.slice(0, 1);
    }

    props.onChange("listOptions", {
      ...listOptions,
      delimiter: nextValue
    });
  };

  const isKeyField =
    checkIsKeyField(props.fieldName) && getFeatureAccess(enumFeaturesList.listFields);

  return (
    <Flex direction={"column"} gap={"12px"}>
      <Text modifier="bold" size="small">
        {appstr("List options")}
      </Text>
      <Checkbox
        id="field-list-checkbox"
        data-testid="field-list-toggle"
        disabled={isKeyField}
        label={appstr("Set list")}
        size="large"
        checked={listOptions.enabled}
        onToggle={handleToggleList}
      />
      <Input
        dataTestId={`${props.dataTestIdPrefix}-list-delimiter`}
        label={appstr("Delimiter")}
        size={"medium"}
        disabled={!listOptions.enabled}
        value={listOptions.delimiter}
        onChange={handleDelimiterChange}
      />
    </Flex>
  );
};

const checkIsKeyField = (fieldName?: string) => {
  const fieldDef = GetFieldDefFromName(fieldName ?? "");
  if (!fieldDef) {
    return false;
  }

  const fieldId = fieldDef.GetFieldID();
  return ["PersonID", "SupervisorID"].includes(fieldId);
};
